<template>
  <div class="help-center-page">
    <!-- Navigation -->
    <div class="nav-container" :class="{ 'scrolled': isScrolled }">
      <nav class="modern-nav">
        <div class="nav-brand" @click="$router.push('/')">
          <img src="/logo.png" alt="logo" class="brand-logo-img" />
          <span class="brand-name">spaceproxy</span>
        </div>

        <div class="nav-links desktop-only">
          <router-link to="/">{{ $t('menu.home') }}</router-link>
          <router-link to="/dynamic-residential-proxy">{{ $t('menu.dynamicProxy') }}</router-link>
          <!-- <router-link to="/static-residential-proxy">{{ $t('menu.staticProxyPage') }}</router-link> -->
          <router-link to="/help-center" class="router-link-exact-active">{{ $t('menu.helpCenter') }}</router-link>
        </div>

        <div class="nav-actions">
          <a-dropdown :trigger="['click']" placement="bottomCenter">
            <button class="action-icon-btn">
              <a-icon type="global" />
              <span class="lang-text">{{ $i18n.locale === 'zh-CN' ? '中' : 'EN' }}</span>
            </button>
            <a-menu slot="overlay" @click="handleLangClick" class="custom-dark-menu">
              <a-menu-item key="zh-CN">🇨🇳 简体中文</a-menu-item>
              <a-menu-item key="en-US">🇺🇸 English</a-menu-item>
            </a-menu>
          </a-dropdown>

          <template v-if="!isLoggedIn">
            <button class="btn-ghost desktop-only" @click="$router.push('/login')">{{ $t('menu.login') }}</button>
            <button class="btn-primary desktop-only" @click="$router.push('/register')">{{ $t('menu.register') }}</button>
          </template>
          <template v-else>
            <a-dropdown :trigger="['hover']" placement="bottomRight">
              <button class="user-pill desktop-only" @click="$router.push('/dashboard/auth')">
                <div class="u-avatar"><a-icon type="user" /></div>
                <span class="u-name">{{ username }}</span>
              </button>
              <a-menu slot="overlay" class="custom-dark-menu">
                <a-menu-item key="dashboard" @click="$router.push('/dashboard/auth')">
                  <a-icon type="appstore" /> {{ $t('menu.dashboard') }}
                </a-menu-item>
                <a-menu-divider />
                <a-menu-item key="logout" @click="handleLogout" class="danger-text">
                  <a-icon type="logout" /> {{ $t('menu.logout') }}
                </a-menu-item>
              </a-menu>
            </a-dropdown>
          </template>

          <button class="action-icon-btn mobile-only" @click="showMobileMenu = true">
            <a-icon type="menu" />
          </button>
        </div>
      </nav>
    </div>

    <!-- Mobile Drawer -->
    <a-drawer
      placement="right"
      :visible="showMobileMenu"
      @close="showMobileMenu = false"
      :closable="false"
      width="100%"
      wrapClassName="dark-mobile-drawer"
    >
      <div class="drawer-inner">
        <div class="d-header">
          <div class="nav-brand">
            <img src="/logo.png" alt="logo" class="brand-logo-img" />
            <span class="brand-name">spaceproxy</span>
          </div>
          <button class="close-btn" @click="showMobileMenu = false">
            <a-icon type="close" />
          </button>
        </div>
        <div class="d-menu">
          <router-link to="/" @click.native="showMobileMenu = false">{{ $t('menu.home') }}</router-link>
          <router-link to="/dynamic-residential-proxy" @click.native="showMobileMenu = false">{{ $t('menu.dynamicProxy') }}</router-link>
          <!-- <router-link to="/static-residential-proxy" @click.native="showMobileMenu = false">{{ $t('menu.staticProxyPage') }}</router-link> -->
          <router-link to="/help-center" @click.native="showMobileMenu = false">{{ $t('menu.helpCenter') }}</router-link>
          <a @click="toggleMobileLang"><a-icon type="global" /> {{ $i18n.locale === 'zh-CN' ? 'Switch to English' : '切换到中文' }}</a>
        </div>
        <div class="d-footer">
          <template v-if="!isLoggedIn">
            <button class="btn-ghost full-width" @click="$router.push('/login'); showMobileMenu = false">{{ $t('menu.login') }}</button>
            <button class="btn-primary full-width mt-4" @click="$router.push('/register'); showMobileMenu = false">{{ $t('menu.register') }}</button>
          </template>
          <template v-else>
            <div class="d-user">
              <div class="u-avatar"><a-icon type="user" /></div>
              <span>{{ username }}</span>
            </div>
            <button class="btn-ghost full-width mt-4" @click="$router.push('/dashboard/auth'); showMobileMenu = false">{{ $t('menu.dashboard') }}</button>
            <button class="btn-danger full-width mt-4" @click="handleLogout(); showMobileMenu = false">{{ $t('menu.logout') }}</button>
          </template>
        </div>
      </div>
    </a-drawer>

    <!-- Help Hero -->
    <section class="help-hero">
      <div class="hero-bg">
        <div class="glow-blob center-blob"></div>
        <div class="grid-overlay"></div>
      </div>
      <div class="container relative-z">
        <h1>{{ $t('help.hero.title') }}</h1>
        <p>{{ $t('help.hero.subtitle') }}</p>
      </div>
    </section>

    <!-- Quick Guides Section -->
    <section class="quick-guides-section scroll-reveal">
      <div class="container">
        <h2 class="section-title">{{ $t('help.quickGuides.title') }}</h2>
        <div class="guides-grid">
          <div class="guide-card" @click="$router.push('/register')">
            <div class="g-icon"><a-icon type="rocket" /></div>
            <h3>{{ $t('help.quickGuides.g1Title') }}</h3>
            <p>{{ $t('help.quickGuides.g1Desc') }}</p>
            <div class="g-arrow"><a-icon type="arrow-right" /></div>
          </div>
          <div class="guide-card">
            <div class="g-icon" style="color: #3b82f6; background: rgba(59, 130, 246, 0.1);"><a-icon type="setting" /></div>
            <h3>{{ $t('help.quickGuides.g2Title') }}</h3>
            <p>{{ $t('help.quickGuides.g2Desc') }}</p>
            <div class="g-arrow"><a-icon type="arrow-right" /></div>
          </div>
          <div class="guide-card">
            <div class="g-icon" style="color: #f59e0b; background: rgba(245, 158, 11, 0.1);"><a-icon type="credit-card" /></div>
            <h3>{{ $t('help.quickGuides.g4Title') }}</h3>
            <p>{{ $t('help.quickGuides.g4Desc') }}</p>
            <div class="g-arrow"><a-icon type="arrow-right" /></div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="faq-section scroll-reveal">
      <div class="content-wrapper">
        <div class="faq-tabs-container">
          <a-tabs default-active-key="1" type="card" :tabBarGutter="10">
            <a-tab-pane v-for="category in faqCategories" :key="category.key" :tab="category.name">
              <a-collapse accordion :bordered="false" class="faq-collapse">
                <a-collapse-panel v-for="(item, index) in category.items" :key="index" :header="item.q">
                  <p v-html="item.a"></p>
                </a-collapse-panel>
              </a-collapse>
            </a-tab-pane>
          </a-tabs>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="contact-section scroll-reveal">
      <div class="container">
        <div class="contact-card">
          <h2>{{ $t('help.contact.title') }}</h2>
          <p>{{ $t('help.contact.desc') }}</p>
          <div class="contact-options">
            <div class="contact-info-list" style="display: flex; flex-direction: column; gap: 16px; align-items: center; font-size: 18px; color: #f4f4f5;">
              <div class="contact-row" style="display: flex; align-items: center; gap: 12px;">
                <a-icon type="wechat" style="color: #07c160; font-size: 24px;" />
                <span></span>
              </div>
              <!-- <div class="contact-row" style="display: flex; align-items: center; gap: 12px;">
                <a-icon type="qq" style="color: #12b7f5; font-size: 24px;" />
                <span>QQ: 1337007470</span>
              </div> -->
              <!-- <div class="contact-row" style="display: flex; align-items: center; gap: 12px;">
                <a-icon type="send" style="color: #0088cc; font-size: 24px;" />
                <span>Telegram: <a href="https://t.me/+86Q95082323" target="_blank" style="color: inherit; text-decoration: underline;">@Q95082323</a></span>
              </div> -->
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Connect Float -->
    <!-- <div class="connect-float">
      <a href="tencent://message/?uin=1337007470" class="c-btn qq-btn">
        <a-icon type="qq" />
        <span class="c-tooltip">QQ</span>
      </a>
      <a href="javascript:void(0)" class="c-btn wechat-btn">
        <a-icon type="wechat" />
        <span class="c-tooltip">Q95082323</span>
      </a>
    </div> -->

    <!-- Footer -->
    <footer class="dark-footer">
      <div class="container">
        <div class="f-grid">
          <div class="f-brand">
            <div class="nav-brand">
              <img src="/logo.png" alt="logo" class="brand-logo-img" />
              <span class="brand-name">spaceproxy</span>
            </div>
            <p>{{ $t('home.footer.desc') }}</p>
          </div>
          <div class="f-links">
            <div class="f-col">
              <h4>{{ $t('home.footer.products') }}</h4>
              <router-link to="/dynamic-residential-proxy">{{ $t('menu.dynamicProxy') }}</router-link>
              <router-link to="/static-residential-proxy">{{ $t('menu.staticProxyPage') }}</router-link>
            </div>
            <div class="f-col">
              <h4>{{ $t('home.footer.support') }}</h4>
              <router-link to="/help-center">{{ $t('menu.helpCenter') }}</router-link>
            </div>
            <div class="f-col">
              <h4>{{ $t('home.footer.contact') }}</h4>
              <div class="contact-item">
                <a-icon type="wechat" />
                <span></span>
              </div>
              <!-- <div class="contact-item">
                <a-icon type="qq" />
                <span>QQ: </span>
              </div> -->
            </div>
          </div>
        </div>
        <div class="f-bottom">
          <p>{{ $t('home.footer.copyright', { year: new Date().getFullYear() }) }}</p>
        </div>
      </div>
    </footer>

    <!-- Back Top -->
    <a-back-top :visibilityHeight="300">
      <div class="back-top-btn">
        <a-icon type="arrow-up" />
      </div>
    </a-back-top>
  </div>
</template>

<script>
export default {
  name: 'HelpCenter',
  data() {
    return {
      isLoggedIn: false,
      username: '',
      showMobileMenu: false,
      isScrolled: false
    };
  },
  computed: {
    faqCategories() {
      return [
        {
          name: this.$t('help.tabs.intro'),
          key: '1',
          items: [
            { q: this.$t('help.faq.q1'), a: this.$t('help.faq.a1') },
            { q: this.$t('help.faq.q2'), a: this.$t('help.faq.a2') },
            { q: this.$t('help.faq.q3'), a: this.$t('help.faq.a3') },
            { q: this.$t('help.faq.q4'), a: this.$t('help.faq.a4') },
            { q: this.$t('help.faq.q5'), a: this.$t('help.faq.a5') },
            { q: this.$t('help.faq.q6'), a: this.$t('help.faq.a6') },
            { q: this.$t('help.faq.q7'), a: this.$t('help.faq.a7') },
            { q: this.$t('help.faq.q8'), a: this.$t('help.faq.a8') },
            { q: this.$t('help.faq.q9'), a: this.$t('help.faq.a9') },
            { q: this.$t('help.faq.q10'), a: this.$t('help.faq.a10') },
            { q: this.$t('help.faq.q11'), a: this.$t('help.faq.a11') }
          ]
        },
        {
          name: this.$t('help.tabs.pricing'),
          key: '2',
          items: [
            { q: this.$t('help.faq.q12'), a: this.$t('help.faq.a12') },
            { q: this.$t('help.faq.q13'), a: this.$t('help.faq.a13') },
            { q: this.$t('help.faq.q14'), a: this.$t('help.faq.a14') },
            { q: this.$t('help.faq.q15'), a: this.$t('help.faq.a15') },
            { q: this.$t('help.faq.q16'), a: this.$t('help.faq.a16') },
            { q: this.$t('help.faq.q17'), a: this.$t('help.faq.a17') },
            { q: this.$t('help.faq.q18'), a: this.$t('help.faq.a18') },
            { q: this.$t('help.faq.q19'), a: this.$t('help.faq.a19') },
            { q: this.$t('help.faq.q20'), a: this.$t('help.faq.a20') },
            { q: this.$t('help.faq.q21'), a: this.$t('help.faq.a21') },
            { q: this.$t('help.faq.q22'), a: this.$t('help.faq.a22') },
            { q: this.$t('help.faq.q23'), a: this.$t('help.faq.a23') },
            { q: this.$t('help.faq.q24'), a: this.$t('help.faq.a24') }
          ]
        },
        {
          name: this.$t('help.tabs.account'),
          key: '3',
          items: [
            { q: this.$t('help.faq.q25'), a: this.$t('help.faq.a25') },
            { q: this.$t('help.faq.q26'), a: this.$t('help.faq.a26') },
            { q: this.$t('help.faq.q27'), a: this.$t('help.faq.a27') },
            { q: this.$t('help.faq.q28'), a: this.$t('help.faq.a28') }
          ]
        },
        {
          name: this.$t('help.tabs.troubleshoot'),
          key: '4',
          items: [
            { q: this.$t('help.faq.q29'), a: this.$t('help.faq.a29') },
            { q: this.$t('help.faq.q30'), a: this.$t('help.faq.a30') }
          ]
        }
      ];
    }
  },
  mounted() {
    this.checkLoginStatus();
    this.initScrollReveal();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    handleLangClick({ key }) {
      this.$i18n.locale = key;
      localStorage.setItem('language', key);
    },
    toggleMobileLang() {
      const newLang = this.$i18n.locale === 'zh-CN' ? 'en-US' : 'zh-CN';
      this.$i18n.locale = newLang;
      localStorage.setItem('language', newLang);
    },
    handleContactClick() {
      this.$message.info('请联系');
    },
    checkLoginStatus() {
      const token = localStorage.getItem('Token');
      const username = localStorage.getItem('userName');
      if (token && username) {
        this.isLoggedIn = true;
        this.username = username;
      }
    },
    handleLogout() {
      localStorage.clear();
      this.isLoggedIn = false;
      this.username = '';
      this.$message.success('退出登录成功');
      this.$router.push('/login');
    },
    initScrollReveal() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      setTimeout(() => {
        const animatedElements = document.querySelectorAll('.scroll-reveal');
        animatedElements.forEach(el => {
          el.style.opacity = '0';
          el.style.transform = 'translateY(40px)';
          el.style.transition = 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)';
          observer.observe(el);
        });
      }, 100);
    }
  }
};
</script>

<style scoped lang="less">
/* Variables */
@bg-dark: #09090b;
@card-bg: #18181b;
@border: #27272a;
@primary: #8b5cf6;
@primary-hover: #a855f7;
@accent: #3b82f6;
@text-main: #f4f4f5;
@text-muted: #a1a1aa;

.help-center-page {
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  background-color: @bg-dark;
  color: @text-main;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

button {
  outline: none;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Animations */
.scroll-reveal.is-visible {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

/* Navigation */
.nav-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 20px;
  transition: all 0.4s ease;
  
  &.scrolled {
    padding: 10px 20px;
    
    .modern-nav {
      background: rgba(24, 24, 27, 0.8);
      backdrop-filter: blur(12px);
      border-color: @border;
      box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    }
  }
}

.modern-nav {
  max-width: 1200px;
  margin: 0 auto;
  height: 64px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  transition: all 0.4s ease;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  
  .brand-logo-img {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    object-fit: cover;
  }
  
  .brand-icon {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, @primary, @accent);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    
    svg { width: 20px; height: 20px; }
  }
  
  .brand-name {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.5px;
    color: @text-main;
  }
}

.nav-links {
  display: flex;
  gap: 32px;
  
  a {
    color: @text-muted;
    font-weight: 500;
    font-size: 15px;
    transition: color 0.3s;
    
    &:hover, &.router-link-exact-active {
      color: @text-main;
    }
  }
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.action-icon-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  color: @text-muted;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.3s;
  
  &:hover {
    background: @card-bg;
    color: @text-main;
  }
}

.btn-ghost {
  color: @text-main;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  transition: all 0.3s;
  
  &:hover { background: @card-bg; }
}

.btn-primary {
  background: linear-gradient(135deg, @primary, @accent);
  color: #fff;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 10px;
  transition: all 0.3s;
  box-shadow: 0 4px 14px rgba(139, 92, 246, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
  }
  
  &.huge {
    padding: 16px 36px;
    font-size: 18px;
    border-radius: 14px;
  }
}

.user-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background: @card-bg;
  border: 1px solid @border;
  padding: 4px 16px 4px 4px;
  border-radius: 20px;
  color: @text-main;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;
  
  .u-avatar {
    width: 28px;
    height: 28px;
    background: @primary;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
  
  &:hover {
    border-color: @primary;
  }
}

.mobile-only { display: none; }

/* Help Hero */
.help-hero {
  position: relative;
  padding: 180px 24px 100px;
  text-align: center;
  border-bottom: 1px solid @border;
  overflow: hidden;
  
  .hero-bg {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    overflow: hidden;
    z-index: 0;
    
    .glow-blob {
      position: absolute;
      width: 800px;
      height: 800px;
      border-radius: 50%;
      filter: blur(120px);
      opacity: 0.15;
      background: @primary;
      top: -300px;
      left: 50%;
      transform: translateX(-50%);
    }
    
    .grid-overlay {
      position: absolute;
      inset: 0;
      background-image: 
        linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px);
      background-size: 50px 50px;
      mask-image: radial-gradient(ellipse at center, black 0%, transparent 80%);
    }
  }
  
  .relative-z { position: relative; z-index: 1; }
  
  h1 {
    font-size: 56px;
    font-weight: 900;
    color: #fff;
    margin-bottom: 24px;
    letter-spacing: -1px;
  }
  
  p {
    font-size: 20px;
    color: @text-muted;
    margin-bottom: 48px;
  }
}

/* Quick Guides */
.quick-guides-section {
  padding: 100px 0 0;
}
.section-title {
  text-align: center;
  font-size: 32px;
  font-weight: 800;
  color: #fff;
  margin-bottom: 48px;
}
.guides-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
}
.guide-card {
  background: @card-bg;
  border: 1px solid @border;
  border-radius: 20px;
  padding: 32px 24px;
  cursor: pointer;
  transition: all 0.4s;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(139, 92, 246, 0.4);
    background: rgba(255,255,255,0.02);
    
    .g-arrow {
      opacity: 1;
      transform: translateX(0);
      color: @primary;
    }
  }
  
  .g-icon {
    width: 48px;
    height: 48px;
    background: rgba(139, 92, 246, 0.1);
    color: @primary;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    margin-bottom: 24px;
  }
  
  h3 {
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 12px;
  }
  
  p {
    color: @text-muted;
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
  }
  
  .g-arrow {
    position: absolute;
    bottom: 32px;
    right: 24px;
    font-size: 20px;
    opacity: 0;
    transform: translateX(-10px);
    transition: all 0.3s;
  }
}

/* FAQ Section */
.faq-section {
  padding: 100px 24px;
}
.content-wrapper {
  max-width: 1000px;
  margin: 0 auto;
}
.faq-tabs-container {
  background: transparent;
}
.faq-tabs-container /deep/ .ant-tabs-bar {
  border-bottom: none;
  margin-bottom: 48px;
  text-align: center;
}
.faq-tabs-container /deep/ .ant-tabs-nav-container {
  margin-bottom: 0;
}
.faq-tabs-container /deep/ .ant-tabs-nav-wrap {
  margin-bottom: 0;
}
.faq-tabs-container /deep/ .ant-tabs-nav-scroll {
  display: flex;
  justify-content: center;
}
.faq-tabs-container /deep/ .ant-tabs-nav {
  margin: 0;
}
.faq-tabs-container /deep/ .ant-tabs-tab {
  background: transparent !important;
  border: 1px solid @border !important;
  border-radius: 20px;
  margin-right: 12px !important;
  font-size: 16px;
  padding: 10px 32px;
  height: auto;
  transition: all 0.3s;
  color: @text-muted !important;
}
.faq-tabs-container /deep/ .ant-tabs-tab-active {
  background: rgba(139, 92, 246, 0.1) !important;
  color: @primary !important;
  border-color: @primary !important;
}
.faq-collapse {
  background: transparent;
  border: none;
}
.faq-collapse /deep/ .ant-collapse-item {
  border: 1px solid @border !important;
  border-radius: 16px !important;
  margin-bottom: 16px;
  background: @card-bg !important;
  overflow: hidden;
  transition: all 0.3s;
}
.faq-collapse /deep/ .ant-collapse-item:hover {
  border-color: rgba(139, 92, 246, 0.4) !important;
}
.faq-collapse /deep/ .ant-collapse-header {
  font-size: 16px;
  font-weight: 600;
  color: #fff !important;
  //padding: 20px 24px !important;
  display: flex;
  align-items: center;
  background: transparent !important;
}
.faq-collapse /deep/ .ant-collapse-header .anticon {
  color: @primary;
  margin-right: 12px;
}
.faq-collapse /deep/ .ant-collapse-content {
  background: transparent !important;
  border-top: 1px solid @border !important;
}
.faq-collapse /deep/ .ant-collapse-content-box {
  padding: 20px 24px 24px !important;
  color: @text-muted !important;
  line-height: 1.8;
  font-size: 15px;
}
.faq-collapse /deep/ .ant-collapse-content-box p {
  color: @text-muted !important;
  margin: 0;
}

/* Contact */
.contact-section {
  padding: 0 24px 100px;
}
.contact-card {
  max-width: 800px;
  margin: 0 auto;
  background: @card-bg;
  border: 1px solid @border;
  border-radius: 24px;
  padding: 60px 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(circle at top, rgba(139, 92, 246, 0.1), transparent 70%);
    pointer-events: none;
  }
}
.contact-card h2 {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 16px;
  color: #fff;
  position: relative;
  z-index: 1;
}
.contact-card p {
  color: @text-muted;
  font-size: 18px;
  margin-bottom: 40px;
  position: relative;
  z-index: 1;
}
.contact-options {
  position: relative;
  z-index: 1;
}

/* Floating Connect */
.connect-float {
  position: fixed; right: 24px; bottom: 100px; z-index: 99;
  display: flex; flex-direction: column; gap: 12px;
  
  .c-btn {
    width: 50px; height: 50px;
    background: @card-bg; border: 1px solid @border; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: @text-main; font-size: 20px;
    position: relative; transition: all 0.3s;
    box-shadow: 0 4px 20px rgba(0,0,0,0.5);
    
    &.telegram-btn:hover { border-color: #0088cc; color: #0088cc; transform: scale(1.1); }
    &.qq-btn:hover { border-color: #12b7f5; color: #12b7f5; transform: scale(1.1); }
    &.wechat-btn:hover { border-color: #07c160; color: #07c160; transform: scale(1.1); }
    
    &:hover {
      .c-tooltip { opacity: 1; visibility: visible; transform: translateX(0); }
    }
    
    .c-tooltip {
      position: absolute; right: 60px;
      background: @text-main; color: @bg-dark;
      padding: 6px 12px; border-radius: 6px; font-size: 13px; font-weight: 600;
      white-space: nowrap;
      opacity: 0; visibility: hidden; transform: translateX(10px); transition: all 0.3s;
    }
  }
}

/* Back Top */
.back-top-btn {
  width: 50px; height: 50px;
  background: @card-bg; border: 1px solid @border; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: @text-main; font-size: 20px;
  transition: all 0.3s;
  
  &:hover { background: @primary; border-color: @primary; color: #fff; }
}

/* Footer */
.dark-footer {
  background: #000; border-top: 1px solid @border; padding: 80px 0 40px;
  margin-top: auto;
  
  .f-grid {
    display: flex; justify-content: space-between; gap: 40px; margin-bottom: 60px;
  }
  
  .f-brand {
    max-width: 300px;
    p { color: @text-muted; line-height: 1.6; margin-top: 20px; }
  }
  
  .f-links {
    display: flex; gap: 80px;
    
    .f-col {
      display: flex; flex-direction: column; gap: 16px;
      h4 { color: #fff; font-weight: 700; font-size: 16px; margin: 0; }
      a { color: @text-muted; transition: color 0.3s; &:hover { color: @primary; } }
      .contact-item {
        display: flex;
        align-items: center;
        gap: 8px;
        color: @text-muted;
        font-size: 14px;
        i { color: @primary; font-size: 16px; }
      }
    }
  }
  
  .f-bottom {
    border-top: 1px solid @border; padding-top: 30px; text-align: center;
    p { color: @text-muted; font-size: 14px; margin: 0; }
  }
}

@media (max-width: 1024px) {
  .guides-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .f-grid { flex-direction: column; }
  .f-links { flex-wrap: wrap; gap: 40px; }
}

@media (max-width: 768px) {
  .desktop-only { display: none !important; }
  .mobile-only { display: flex !important; }
  
  .nav-container { padding: 10px; }
  .modern-nav { height: 56px; padding: 0 16px; border-radius: 12px; background: rgba(24, 24, 27, 0.9); }
  
  .help-hero { padding: 120px 24px 60px; }
  .help-hero h1 { font-size: 40px; }
  .help-hero p { font-size: 16px; }
  
  .guides-grid { grid-template-columns: 1fr; }
  
  .faq-tabs-container /deep/ .ant-tabs-nav-scroll {
    overflow-x: auto;
    white-space: nowrap;
  }
  .faq-tabs-container /deep/ .ant-tabs-tab {
    margin-right: 8px !important;
    padding: 6px 16px;
    font-size: 14px;
  }

  .contact-card { padding: 40px 20px; }
  .contact-card h2 { font-size: 24px; }
  .contact-card p { font-size: 16px; }
  
  .f-links { gap: 30px; flex-direction: column; }
}
</style>
